import { useCallback, useMemo } from "react";
import type { HistoryFilter } from "@common/types/auditLog";
import { useGetHistoryDailyCountsQueries } from "@api/auditLog.api";
import { useCurrentWorkspaceMembers } from "@api/workspaceMember.api";
import { ActivityPageTimeline } from "@components/activityTimeline/ActivityPageTimeline";
import type { ActivityTimelineRange } from "@components/activityTimeline/activityTimelineRange.lib";
import type { TimelineSeriesKey } from "@components/activityTimeline/activityTimelineSeries.lib";
import { ACTIVITY_TIMELINE_SERIES } from "@components/activityTimeline/activityTimelineSeries.lib";
import type { ActivityChartDensityControl } from "@components/activityTimeline/useActivityChartDensity";
import { useActivityTimelineCountsParams } from "@components/activityTimeline/useActivityTimelineCountsParams";
import type { DensityChartWindow } from "@design-system/DensityChart/DensityChart.lib";
import type { HistoryFilters } from "@hooks/view/useHistoryPageFiltersCache";
import { CLEARED_ACTOR_FILTERS } from "../utils/historyFilterClears";
import { toSliceActorFilters } from "./historyTimelineFilters";

const describeWorkspaceStart = (start: string) =>
    `Your activity starts on ${start}, when this workspace joined Kick.`;

interface HistoryTimelineProps {
    /** The History list's filter; the counts swap its dates for each chunk's. */
    filter: HistoryFilter;
    range: ActivityTimelineRange;
    previewWindow: DensityChartWindow | null;
    today: string;
    chartDensity: ActivityChartDensityControl;
    onWindowChange: (window: DensityChartWindow) => void;
    onWindowPreview: (window: DensityChartWindow | null) => void;
    onFiltersChange: (filters: Partial<HistoryFilters>) => void;
}

/**
 * The workspace Activity timeline over the workspace's history, following
 * every toolbar filter but whole series picked alone in Updated by, which it
 * draws selected instead, and split by person when Updated by picks 1 to 6
 * people and not All people. A slice click sets Updated by to what the
 * slice counted, and a click on a selected slice clears it.
 */
export function HistoryTimeline({
    filter,
    range,
    previewWindow,
    today,
    chartDensity,
    onWindowChange,
    onWindowPreview,
    onFiltersChange,
}: HistoryTimelineProps) {
    const { data: members } = useCurrentWorkspaceMembers();
    const knownPeople = useMemo(
        () => (members ?? []).map(({ user }) => user),
        [members],
    );
    const { people, chunks, queries, onDrawnDaysChange } =
        useActivityTimelineCountsParams({
            filter,
            unit: chartDensity.shownDensity,
            knownPeople,
            today,
        });
    const counts = useGetHistoryDailyCountsQueries({ queries });
    const toSeriesFilters = useCallback(
        (key: TimelineSeriesKey): Partial<HistoryFilters> =>
            toSliceActorFilters({ key, filter }),
        [filter],
    );

    return (
        <ActivityPageTimeline
            counts={counts}
            chunks={chunks}
            onDrawnDaysChange={onDrawnDaysChange}
            people={people}
            picks={filter}
            series={ACTIVITY_TIMELINE_SERIES}
            closestUnit={chartDensity.closestDensity}
            onShownUnitChange={chartDensity.showDensity}
            range={range}
            previewWindow={previewWindow}
            today={today}
            onWindowChange={onWindowChange}
            onWindowPreview={onWindowPreview}
            toSeriesFilters={toSeriesFilters}
            clearedActorFilters={CLEARED_ACTOR_FILTERS}
            onFiltersChange={onFiltersChange}
            describeHistoryStart={describeWorkspaceStart}
        />
    );
}
